import { LicenseDetailDTO } from 'business/models/Models';
import LicenseService from 'business/services/LicenseService';
import { EFeatureTypes } from 'enums/EFeatureTypes';
import {
  FmlxBanner,
  FmlxButton,
  FmlxCard,
  FmlxModal,
  FmlxRadioButton,
  FmlxTable,
} from 'fmlx-common-ui';
import Cookies from 'js-cookie';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { setCookie, showSnackBar } from 'utils/Global';
import LicenseHeader from './LicenseHeader';
import DateHelper from 'utils/DateHelper';
import { setProductEdition } from 'redux/reducers/experimentReducer';
import { useRMDispatch } from 'redux/hooks';
import { useIsLicenseAutenticatorHealthy } from 'business/licenseValidator/hooks';
import SeatLicenseViolation from './SeatLicenseViolation';

const licenseService = new LicenseService();
const concurrentSessionCols = [
  { Header: 'Edition', accessor: 'edition', hideSort: true },
  { Header: 'Username', accessor: 'userName', hideSort: true },
  { Header: 'Platform', accessor: 'platform', hideSort: true },
  { Header: 'Last Activity', accessor: 'lastActive', hideSort: true },
];

const AvailableSessionLabel: React.FC<{ license: LicenseDetailDTO }> = ({
  license,
}): any => {
  const [sessionListShown, setSessionListShown] = useState(false);
  if (
    license.licType === 'Temporary' ||
    license.licType === 'Site' ||
    license.maxSessionsAllowed <= 0
  ) {
    return '';
  }
  if (license.concurrentSessionList?.length >= license.maxSessionsAllowed) {
    const data = license.concurrentSessionList.map((cl) => ({
      edition: license.edition,
      userName: cl.userName,
      lastActive: DateHelper.formatDateTime(cl.sessionStartTime),
      platform: cl.platform,
    }));
    return (
      <div id="hybrid-selection-available-session-label">
        {sessionListShown && (
          <FmlxModal
            id="hybrid-selection-active-session-modal"
            open
            title="Active Session"
            onCloseClick={() => setSessionListShown(false)}
            customFooter={<area />}
          >
            <FmlxBanner
              id="hybrid-selection-available-session-banner"
              variant={FmlxBanner.Variant.INFO}
              hideCloseButton
              title={`
                Rock Maker is currently running at the maximum limit of ${license.maxSessionsAllowed} locations,
                as per your agreement. Please contact the appropriate user to release a session.`}
            />
            <div
              id="hybrid-selection-available-session-table"
              className="py-5 session-list-container"
            >
              <FmlxTable
                id="hybrid-selection-table-concurrent-session-cols"
                columns={concurrentSessionCols as any}
                data={data}
                allowSelectedRow={false}
              />
            </div>
          </FmlxModal>
        )}
        <span id="hybrid-selection-label-no-session-available">
          No sessions available.{' '}
          <a
            id="hybrid-selection-label-session-list-shown"
            className="text-link"
            onClick={() => setSessionListShown(true)}
          >
            See list
          </a>
        </span>
      </div>
    );
  }
  return `${
    license.maxSessionsAllowed - (license.concurrentSessionList?.length ?? 0)
  } session(s) available`;
};

const isNoSessionAvailable = (license: LicenseDetailDTO): boolean => {
  if (license.maxSessionsAllowed <= 0) {
    return false;
  }
  if (license.concurrentSessionList?.length >= license.maxSessionsAllowed) {
    return true;
  }
  return false;
};

export default function HybridSelection() {
  const [selectedLicense, setSelectedLicense] = useState<LicenseDetailDTO | null>(null);
  const [licenseList, setLicenseList] = useState<LicenseDetailDTO[]>();
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const isLicenseAutenticatorHealthy = useIsLicenseAutenticatorHealthy();

  const dispatch = useRMDispatch();
  const _setProductEdition = useCallback(
    (productEdition: string) => dispatch(setProductEdition(productEdition)),
    [dispatch],
  );

  const navigate = useNavigate();

  function optionSelectedValue({ index }) {
    index = parseInt(index, 10);

    const license = licenseList[index];

    setSelectedLicense(license);
    setSelectedIndex(index);
  }

  async function setPickedLicense() {
    const index = licenseList?.findIndex(
      (l) => l.title === EFeatureTypes.RockMakerLiteTitle,
    );
    if (index === selectedIndex) {
      setCookie(EFeatureTypes.SelectedFeature, EFeatureTypes.RMLite);
    } else {
      if (!selectedLicense) {
        return;
      }
      Cookies.set('selectedLicenseType', selectedLicense.licType);
      Cookies.set('selectedLicenseIndex', selectedLicense.licenseIndex);
      _setProductEdition(selectedLicense.licProduct.edition);
    }
    showSnackBar('License has been loaded successfully.');
    navigate('/', { replace: true });
  }

  useEffect(() => {
    async function getLicenseList() {
      const licList = await licenseService.getHybridLicenseList();
      setLicenseList(licList);
    }

    getLicenseList();
  }, []);

  if (
    licenseList?.every(
      (license) => license.licType == 'Seat' && !isLicenseAutenticatorHealthy,
    )
  ) {
    return <SeatLicenseViolation />;
  }

  return (
    <div id="hybrid-selection-wrapper">
      <FmlxCard
        id="hybrid-selection-card-wrapper"
        disabled={false}
        draggable={false}
        selectable={false}
        selected={false}
        type=""
        variant={FmlxCard.Variant.OUTLINED}
      >
        <FmlxCard.Body id="hybrid-selection-card-body-wrapper">
          <LicenseHeader />
          <h5 id="hybrid-selection-card-license" className="label-primary mb-3">
            License Selection
          </h5>
          <div id="hybrid-selection-body" className="selection-body p-3">
            <div id="hybrid-selection-body-content">
              Please select edition that you want to use:
            </div>
            <FmlxRadioButton
              id="hybrid-selection-body-button"
              selectedIndex={selectedIndex}
              size={FmlxRadioButton.Size.LARGE}
              onChange={optionSelectedValue}
              items={
                licenseList?.map((license) => ({
                  type: 'information',
                  content: license.title,
                  hint: (<AvailableSessionLabel license={license} />) as any,
                  disabled: isNoSessionAvailable(license),
                })) || []
              }
            />
          </div>
        </FmlxCard.Body>
        <FmlxCard.Footer>
          <div
            id="hybrid-selection-card-footer-wrapper"
            className="fields flex-x-center mt-2"
          >
            <FmlxButton
              id="hybrid-selection-card-select-button"
              onClick={setPickedLicense}
              label="SELECT"
              disabled={selectedIndex < 0}
            />
          </div>
        </FmlxCard.Footer>
      </FmlxCard>
    </div>
  );
}
